import { cloneDeep, defaults } from 'lodash';
import { YObject } from '../../../common/y-session';
import { CUSTOM_FABRIC_DATA } from '../../wb-canvas/fabric-utils';
import { BoardItemConfig, CreationContext } from './board-item-configurations';

interface StickyNoteTextSettings {
  fontFamily: string;
  fontSize: number;
  fontWeight: string;
  fontStyle: '' | 'normal' | 'italic' | 'oblique' | undefined;
  underline: boolean;
}

export class StickyNoteConfigurations extends BoardItemConfig {
  // Dimensions
  static readonly DEFAULT_WIDTH = 180;
  static readonly DEFAULT_HEIGHT = 180;

  // Content
  text: string;
  textSettings: StickyNoteTextSettings; // global textbox settings
  textStyles: any = {}; // custom textbox styles
  backgroundColor: string;
  defaultFontSize?: number;

  static defaultBoardItemConfigurations: Partial<BoardItemConfig> = {
    width: StickyNoteConfigurations.DEFAULT_WIDTH,
    height: StickyNoteConfigurations.DEFAULT_HEIGHT,
  };

  static defaultConfigs = {
    text: '',
    backgroundColor: '#FCEB9F',
    textSettings: StickyNoteConfigurations.defaultStickyNoteTextSettings(),
    textStyles: {},
  };

  constructor(
    configurations: Partial<StickyNoteConfigurations> & Partial<BoardItemConfig>,
    globalCreationParams: CreationContext,
  ) {
    super(
      {
        ...StickyNoteConfigurations.defaultBoardItemConfigurations,
        ...configurations,
      },
      globalCreationParams,
    );

    const stickyConfigurations = defaults(configurations, StickyNoteConfigurations.defaultConfigs);
    this.text = stickyConfigurations.text;
    this.backgroundColor = stickyConfigurations.backgroundColor;
    this.textSettings = { ...stickyConfigurations.textSettings };
    this.textStyles = { ...stickyConfigurations.textStyles };
    if (configurations.defaultFontSize) {
      // set the default font size for new created sticky notes
      this.textSettings.fontSize = configurations.defaultFontSize;
    }
  }

  static defaultStickyNoteTextSettings(): StickyNoteTextSettings {
    return {
      fontFamily: 'Source Sans Pro',
      fontSize: 12, // to preserve font size for old sticky notes
      fontWeight: 'normal',
      fontStyle: 'normal',
      underline: false,
    };
  }

  static fromYObject(
    object: YObject,
    globalCreationsParams: CreationContext,
  ): StickyNoteConfigurations {
    const configurations = BoardItemConfig.fromYObject(
      object,
      globalCreationsParams,
      StickyNoteConfigurations.defaultBoardItemConfigurations,
    );
    const customData = object[CUSTOM_FABRIC_DATA];

    if (!customData) {
      throw new Error('Invalid sticky note fabric object - missing custom data');
    }

    // Because previously the prompt was stored in the object, we want to ignore it,
    // to have the prompt processed at the front-end level only.
    const text = customData?.text === 'Start typing…' ? '' : customData?.text;

    return new StickyNoteConfigurations(
      {
        ...configurations,
        top: customData?.top,
        left: customData?.left,
        width: customData?.width,
        height: customData?.height,
        text,
        locked: customData?.locked,
        uid: (object as any).uid,
        vuid: (object as any).vuid,
        ScribbleType: (object as any).ScribbleType,
        backgroundColor: customData.backgroundColor,
        textSettings: cloneDeep(customData?.textSettings),
        textStyles: cloneDeep(customData?.textStyles),
      },
      globalCreationsParams,
    );
  }
}
